import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks';
import { selectPermissionsMap, selectPermissionsStatus } from '@/store/selectors/permissionSelectors';
import { resolveScreenCode } from '@/utils';
import type { PermissionAction, ScreenPermission } from '@/api/domains/authorization';


type PermissionMode = 'hidden' | 'disabled' | 'enabled';

/** Flags returned by `section(name)` for a specific section. */
export interface SectionPermissionResult {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface UsePagePermissionsResult {
  /** Whether the permissions have been loaded from the backend. */
  isLoaded: boolean;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** Generic accessor for any action. */
  can: (action: PermissionAction) => boolean;
  /** Returns 'enabled' if user has the action, otherwise the given
   * fallback ('hidden' | 'disabled'). */
  permissionMode: (action: PermissionAction, fallback?: 'hidden' | 'disabled') => PermissionMode;
  /** Returns section-level permission flags. Defaults to deny-all if section not found. */
  section: (name: string) => SectionPermissionResult;
  /** Shortcut: checks a single action for a specific section. */
  canSection: (name: string, action: PermissionAction) => boolean;
}

const DENY_SECTION: SectionPermissionResult = {
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
};

const DENY_ALL: Omit<UsePagePermissionsResult, 'isLoaded' | 'can' | 'permissionMode' | 'section' | 'canSection'> = {
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
};

/** Hook to access screen-level permissions from the Redux store. */
export function usePagePermissions(screenCode?: string): UsePagePermissionsResult {
  const { pathname } = useLocation();
  const permissionsMap = useAppSelector(selectPermissionsMap);
  const status = useAppSelector(selectPermissionsStatus);

  const resolvedCode = useMemo(
    () => screenCode ?? resolveScreenCode(pathname),
    [screenCode, pathname],
  );

  const permission: ScreenPermission | null = resolvedCode
    ? permissionsMap[resolvedCode] ?? null
    : null;

  const isLoaded = status === 'succeeded';

  return useMemo(() => {
    const flags = permission ?? DENY_ALL;

    const can = (action: PermissionAction): boolean => {
      const actionMap: Record<PermissionAction, boolean> = {
        view: flags.canView,
        create: flags.canCreate,
        edit: flags.canEdit,
        delete: flags.canDelete,
      };
      return actionMap[action] ?? false;
    };

    const permissionMode = (
      action: PermissionAction,
      fallback: 'hidden' | 'disabled' = 'hidden',
    ): PermissionMode => (can(action) ? 'enabled' : fallback);

    const section = (name: string): SectionPermissionResult => {
      if (!permission?.sections) return DENY_SECTION;
      const sectionPermission = permission.sections[name];
      if (!sectionPermission) return DENY_SECTION;
      return {
        canView: sectionPermission.canView,
        canCreate: sectionPermission.canCreate,
        canEdit: sectionPermission.canEdit,
        canDelete: sectionPermission.canDelete,
      };
    };

    const canSection = (name: string, action: PermissionAction): boolean => {
      const sectionFlags = section(name);
      const actionMap: Record<PermissionAction, boolean> = {
        view: sectionFlags.canView,
        create: sectionFlags.canCreate,
        edit: sectionFlags.canEdit,
        delete: sectionFlags.canDelete,
      };
      return actionMap[action] ?? false;
    };

    return {
      isLoaded,
      canView: flags.canView,
      canCreate: flags.canCreate,
      canEdit: flags.canEdit,
      canDelete: flags.canDelete,
      can,
      permissionMode,
      section,
      canSection,
    };
  }, [permission, isLoaded]);
}
